import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Camera, UserPlus, UserCheck, Clock, MessageCircle, BadgeCheck, Pencil, Check, Video, MoreHorizontal, Flag, VolumeX, Ban, Mail, Phone } from "lucide-react";
import { format } from "date-fns";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/components/Layout";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import PostCard from "@/components/PostCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TrustChip = ({ ok, icon: Icon, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
    <Icon className="w-3 h-3" />{ok ? label : label.replace("confirmed", "not confirmed")}
  </span>
);

export default function Profile() {
  const { userId } = useParams();
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const { openChat, openVerify, openManage, startCall, openReport, confirmAction } = useUI();
  const avatarRef = useRef(null);
  const coverRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "" });
  const isMe = userId === user.user_id;

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => (await api.get(`/users/${userId}`)).data,
  });

  const { data: posts = [] } = useQuery({
    queryKey: ["user-posts", userId],
    queryFn: async () => (await api.get(`/posts/user/${userId}`)).data,
  });

  const uploadTo = async (file, field) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const { data: updated } = await api.put("/users/me", { [field]: data.url });
    setUser(updated);
    qc.invalidateQueries({ queryKey: ["profile", userId] });
    toast.success("Updated!");
  };

  const addFriend = async () => {
    await api.post(`/friends/request/${userId}`);
    toast.success("Friend request sent");
    qc.invalidateQueries({ queryKey: ["profile", userId] });
    qc.invalidateQueries({ queryKey: ["suggestions"] });
  };

  const relate = async (kind, undo) => {
    if (kind === "block" && !undo && !(await confirmAction({ title: `Block ${profile.name}?`, description: "They won't be able to see your posts, message, call or friend you. They won't be notified.", actionLabel: "Block" }))) return;
    try {
      if (undo) await api.delete(`/users/${userId}/${kind}`);
      else await api.post(`/users/${userId}/${kind}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return;
    }
    toast.success(`${profile.name} ${undo ? (kind === "block" ? "unblocked" : "unmuted") : (kind === "block" ? "blocked" : "muted")}`);
    qc.invalidateQueries({ queryKey: ["profile", userId] });
    qc.invalidateQueries({ queryKey: ["user-posts", userId] });
    qc.invalidateQueries({ queryKey: ["feed"] });
    qc.invalidateQueries({ queryKey: ["relations"] });
  };

  const saveEdit = async () => {
    const { data } = await api.put("/users/me", form);
    setUser(data);
    qc.invalidateQueries({ queryKey: ["profile", userId] });
    setEditOpen(false);
    toast.success("Profile updated");
  };

  if (isLoading || !profile) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#8012C6] animate-spin" /></div>;

  const cover = mediaUrl(profile.cover_url);

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="relative h-44 sm:h-56 bg-gradient-to-br from-[#6C0BA9] via-[#8012C6] to-[#E024A3]">
          {cover && <img src={cover} alt="" className="w-full h-full object-cover" />}
          {isMe && (
            <>
              <input ref={coverRef} type="file" accept="image/*" hidden data-testid="cover-file" onChange={(e) => e.target.files[0] && uploadTo(e.target.files[0], "cover_url")} />
              <button data-testid="edit-cover-btn" onClick={() => coverRef.current?.click()} className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-black/50 text-white text-sm font-medium hover:bg-black/70 transition-colors">
                <Camera className="w-4 h-4" /> Cover
              </button>
            </>
          )}
        </div>

        <div className="px-5 pb-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="relative -mt-12 z-10">
              <div className="rounded-full border-4 border-card">
                <Avatar user={profile} size={112} />
              </div>
              {isMe && (
                <>
                  <input ref={avatarRef} type="file" accept="image/*" hidden data-testid="avatar-file" onChange={(e) => e.target.files[0] && uploadTo(e.target.files[0], "avatar_url")} />
                  <button data-testid="edit-avatar-btn" onClick={() => avatarRef.current?.click()} className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-[#6C0BA9] text-white flex items-center justify-center hover:bg-[#8012C6] transition-colors border-2 border-card">
                    <Camera className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>

            <div className="order-last w-full min-w-0">
              <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2">{profile.name}{profile.verified && <VerifiedBadge size={20} />}</h1>
              <p className="text-sm text-muted-foreground">{profile.friend_count} friends · {profile.post_count} posts</p>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="trust-signals">
                <TrustChip ok={profile.email_verified} icon={Mail} label="Email confirmed" testid="trust-email" />
                <TrustChip ok={profile.phone_verified} icon={Phone} label="Phone confirmed" testid="trust-phone" />
                {profile.created_at && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Joined {format(new Date(profile.created_at), "MMM yyyy")}</span>}
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-3">
              {isMe ? (
                <>
                  <Button data-testid="edit-profile-btn" onClick={() => { setForm({ name: profile.name, bio: profile.bio || "" }); setEditOpen(true); }} variant="outline" className="rounded-xl border-border gap-2"><Pencil className="w-4 h-4" />Edit</Button>
                  {profile.verified ? (
                    <Button data-testid="manage-badge-btn" onClick={openManage} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white gap-2"><BadgeCheck className="w-4 h-4" />Manage Badge</Button>
                  ) : (
                    <Button data-testid="profile-verify-btn" onClick={openVerify} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white gap-2"><BadgeCheck className="w-4 h-4" />Get Verified</Button>
                  )}
                </>
              ) : profile.is_blocked || profile.blocked_me ? (
                <>
                  {profile.is_blocked && (
                    <Button data-testid="unblock-btn" onClick={() => relate("block", true)} variant="outline" className="rounded-xl gap-2"><Ban className="w-4 h-4" />Unblock</Button>
                  )}
                </>
              ) : (
                <>
                  {profile.friend_status === "none" && (
                    <Button data-testid="add-friend-btn" onClick={addFriend} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white gap-2"><UserPlus className="w-4 h-4" />Add Friend</Button>
                  )}
                  {profile.friend_status === "outgoing" && (
                    <Button data-testid="request-sent-btn" disabled variant="outline" className="rounded-xl gap-2"><Clock className="w-4 h-4" />Requested</Button>
                  )}
                  {profile.friend_status === "incoming" && (
                    <Button data-testid="respond-friend-btn" onClick={() => (window.location.href = "/friends")} variant="outline" className="rounded-xl gap-2"><UserCheck className="w-4 h-4" />Respond</Button>
                  )}
                  {profile.friend_status === "friends" && (
                    <Button data-testid="friends-badge-btn" disabled variant="outline" className="rounded-xl gap-2"><UserCheck className="w-4 h-4" />Friends</Button>
                  )}
                  <Button data-testid="message-btn" onClick={() => openChat(profile)} variant="outline" className="rounded-xl border-border gap-2"><MessageCircle className="w-4 h-4" />Message</Button>
                  <Button data-testid="video-call-btn" onClick={() => startCall("direct", profile.user_id)} variant="outline" className="rounded-xl border-border gap-2"><Video className="w-4 h-4" />Call</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid="profile-more-btn" variant="outline" className="rounded-xl border-border px-3"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                      <DropdownMenuItem data-testid="profile-report" onClick={() => openReport({ type: "user", id: profile.user_id, label: "account" })} className="cursor-pointer"><Flag className="w-4 h-4 mr-2" />Report account</DropdownMenuItem>
                      <DropdownMenuItem data-testid="profile-mute" onClick={() => relate("mute", profile.is_muted)} className="cursor-pointer"><VolumeX className="w-4 h-4 mr-2" />{profile.is_muted ? "Unmute" : "Mute"}</DropdownMenuItem>
                      <DropdownMenuItem data-testid="profile-block" onClick={() => relate("block", false)} className="cursor-pointer text-red-400"><Ban className="w-4 h-4 mr-2" />Block</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>

          {profile.bio && <p className="mt-4 text-sm leading-relaxed">{profile.bio}</p>}
        </div>
      </div>

      <div className="space-y-6">
        {(profile.is_blocked || profile.blocked_me) ? (
          <div data-testid="blocked-banner" className="bg-card border border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">
            {profile.is_blocked ? "You blocked this person. Unblock them to see their posts." : "This profile isn't available."}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">No posts yet.</div>
        ) : (
          posts.map((p) => <PostCard key={p.id} post={p} />)
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Name</label>
              <Input data-testid="edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-input border-border rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Bio</label>
              <textarea data-testid="edit-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="mt-1 w-full bg-input border border-border rounded-xl px-3 py-2 text-sm resize-none outline-none focus:border-[#6C0BA9]" />
            </div>
            <Button data-testid="save-profile-btn" onClick={saveEdit} className="w-full rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white gap-2"><Check className="w-4 h-4" />Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
